/**
 * Slide Label Printer Service
 *
 * Sends slide label print commands via the Bridge system.
 * The bridge desktop app receives the command and forwards the XML label
 * content to the SlideMate AS printer.
 */

import { bridgeService } from "./bridgeService";
import { deviceBridgeService } from "./deviceBridge.service";
import { slidePrintRoutingService } from "./slidePrintRouting.service";
import {
  isUsablePatientLabelName,
  MISSING_PATIENT_LABEL_NAME_ERROR,
} from "../utils/patientLabelName";
import {
  formatDateOnlyForDisplay,
  requireDateOnlyString,
  type DateOnlyString,
} from "../utils/dateOnly";
import { workstationPreferenceService } from "./workstationPreference.service";

export interface SlideLabelData {
  caseNumber: string;
  patientName: string;
  patientDOB?: DateOnlyString;
  specimenLetter: string;
  blockNumber: string;
  slideNumber: string;
  stainCode: string;
  barcode: string; // Format: {caseNumber};{specimenLetter};{blockNumber};{slideNumber}
  dateReceived?: DateOnlyString;
  isControlSlide?: boolean;
  controlPracticeName?: string;
}

export type PrinterStatus =
  | "folder-selected" // bridge device configured (kept for backward compat)
  | "no-folder" // no bridge device selected (kept for backward compat)
  | "browser-print" // unused, kept for backward compat
  | "unsupported";

/**
 * Human-readable slide identity is specimen + block only. Slide number remains
 * machine-readable barcode data and must never be added to this marker.
 */
export function buildSpecimenBlockDisplayMarker(
  label: Pick<SlideLabelData, "specimenLetter" | "blockNumber">
): string {
  const specimen = label.specimenLetter.trim();
  const block = label.blockNumber.trim();
  return `${specimen}${block}`;
}

export interface PrintResult {
  success: boolean;
  method: "bridge" | "file-system" | "browser";
  count: number;
  error?: string;
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

export function buildXmlContent(label: SlideLabelData): string {
  const labName = "Gulf Coast Pathologists";
  const dateReceived = label.dateReceived
    ? formatDateOnlyForDisplay(
        requireDateOnlyString(label.dateReceived, "Date received")
      )
    : "";
  const displayMarker = escapeXml(buildSpecimenBlockDisplayMarker(label));
  return [
    '<?xml version="1.0" encoding="utf-8"?>',
    '<Material name="Requisition">',
    "<PrinterName>SlideMate AS</PrinterName>",
    "<PageProperties>",
    '<Property name="Magazine">1</Property>',
    '<Property name="PageWidth">1</Property>',
    '<Property name="PageHeight">1</Property>',
    '<Property name="MarginTop">0</Property>',
    '<Property name="MarginLeft">0</Property>',
    '<Property name="Orientation">Portrait</Property>',
    '<Property name="MultiLabel">True</Property>',
    '<Property name="Rows">4</Property>',
    '<Property name="Cols">1</Property>',
    "</PageProperties>",
    "<Fields>",
    `<Field name="CaseNumber" type="text" x="5" y="2" length="13" font="Arial" size="7" bold="True" italic="False" multiline="false" maxlinelength="13">${escapeXml(
      label.caseNumber
    )}</Field>`,
    `<Field name="SlideMarker" type="text" x="50" y="2" length="8" font="Arial" size="7" bold="True" italic="False" multiline="false" maxlinelength="8">${displayMarker}</Field>`,
    // These remain data-only so the four-part barcode retains true slide identity.
    `<Field name="Specimen" type="data">${escapeXml(
      label.specimenLetter
    )}</Field>`,
    `<Field name="BlockID" type="data">${escapeXml(label.blockNumber)}</Field>`,
    `<Field name="SlideID" type="data">${escapeXml(label.slideNumber)}</Field>`,
    `<Field name="PatientName" type="text" x="5" y="12" length="25" font="Arial" size="5" bold="True" italic="False" multiline="false" maxlinelength="25">${escapeXml(
      label.patientName
    )}</Field>`,
    `<Field name="Stain" type="text" x="10" y="18" length="16" font="Arial" size="7" bold="True" italic="False" multiline="false" maxlinelength="16">${escapeXml(
      label.stainCode
    )}</Field>`,
    '<Field name="BarCode0" type="barcode" x="5" y="27" width="21" height="21" scale="100" barcodetype="DataMatrix" fieldseparator=";">',
    "<BarcodeFields>",
    "<BarcodeField>CaseNumber</BarcodeField>",
    "<BarcodeField>Specimen</BarcodeField>",
    "<BarcodeField>BlockID</BarcodeField>",
    "<BarcodeField>SlideID</BarcodeField>",
    "</BarcodeFields>",
    "</Field>",
    `<Field name="DateReceived" type="text" x="33" y="27" length="13" font="Arial" size="5" bold="True" italic="False" multiline="false" maxlinelength="10">${dateReceived}</Field>`,
    `<Field name="LabName" type="text" x="8" y="49" length="23" font="Arial" size="4" bold="False" italic="False" multiline="false" maxlinelength="23">${escapeXml(
      labName
    )}</Field>`,
    "</Fields>",
    "</Material>",
  ].join("\n");
}

const SLIDE_DEVICE_KEY = "slide-printer-device-id";
const SLIDE_DEVICE_NAME_KEY = "slide-printer-device-name";

class SlideLabelPrinterService {
  private deviceId: string | null = null;

  private deviceName: string | null = null;

  private currentStatus: PrinterStatus = "no-folder";

  private statusListeners: Set<(status: PrinterStatus) => void> = new Set();

  constructor() {
    this.restoreDevice();
    workstationPreferenceService.onChange(() => this.notifyStatusChange());
  }

  private restoreDevice(): void {
    try {
      const selectedDeviceId =
        workstationPreferenceService.getSelectedDeviceId("slide_printer");
      const selectedDeviceName =
        workstationPreferenceService.getSelectedDeviceName("slide_printer");
      if (selectedDeviceId) {
        this.deviceId = selectedDeviceId;
        this.deviceName = selectedDeviceName;
        this.currentStatus = "folder-selected";
        return;
      }

      const storedId = localStorage.getItem(SLIDE_DEVICE_KEY);
      const storedName = localStorage.getItem(SLIDE_DEVICE_NAME_KEY);
      if (storedId) {
        this.deviceId = storedId;
        this.deviceName = storedName;
        this.currentStatus = "folder-selected";
      }
    } catch {
      // localStorage unavailable; ignore
    }
  }

  get status(): PrinterStatus {
    if (
      workstationPreferenceService.hasInvalidPreferredPrinter("slide_printer")
    ) {
      return "no-folder";
    }
    if (workstationPreferenceService.getPreferredPrinter("slide_printer")) {
      return "folder-selected";
    }
    return this.currentStatus;
  }

  /**
   * Returns the configured bridge device name (replaces the old folder name).
   */
  get folderName(): string | null {
    return (
      workstationPreferenceService.getPreferredPrinter("slide_printer")
        ?.deviceName ?? this.deviceName
    );
  }

  get isFolderSelected(): boolean {
    return this.status === "folder-selected";
  }

  /**
   * Returns the configured bridge device ID for external use.
   */
  get selectedDeviceId(): string | null {
    return (
      workstationPreferenceService.getPreferredPrinter("slide_printer")
        ?.deviceId ?? this.deviceId
    );
  }

  onStatusChange(listener: (status: PrinterStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private notifyStatusChange(): void {
    this.statusListeners.forEach((listener) => listener(this.status));
  }

  /**
   * Configure the bridge device to use for slide printing.
   */
  setDevice(deviceId: string, deviceName: string): void {
    this.deviceId = deviceId;
    this.deviceName = deviceName;
    this.currentStatus = "folder-selected";
    try {
      localStorage.setItem(SLIDE_DEVICE_KEY, deviceId);
      localStorage.setItem(SLIDE_DEVICE_NAME_KEY, deviceName);
    } catch {
      // localStorage unavailable; ignore
    }
    this.notifyStatusChange();
  }

  /**
   * @deprecated Use setDevice() instead. Kept for backward compatibility.
   * Now auto-detects the first available slide_printer from the bridge.
   */
  async selectOutputDirectory(): Promise<boolean> {
    try {
      const workstationsRes = await bridgeService.getWorkstations({
        status: "online",
      });
      const workstations = workstationsRes.data.data;
      if (workstations.length === 0) return false;

      for (let i = 0; i < workstations.length; i += 1) {
        const ws = workstations[i];
        // eslint-disable-next-line no-await-in-loop
        const devicesRes = await bridgeService.getDevices(ws.id, {
          deviceType: "slide_printer",
        });
        const devices = devicesRes.data.data;
        if (devices.length > 0) {
          this.setDevice(devices[0].id, devices[0].name);
          return true;
        }
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Send slide labels to the bridge for printing.
   */
  async printSlideLabels(
    labels: SlideLabelData[],
    explicitDeviceId?: string
  ): Promise<PrintResult> {
    if (labels.length === 0) {
      return {
        success: false,
        method: "bridge",
        count: 0,
        error: "No labels to print",
      };
    }

    if (labels.some((label) => !isUsablePatientLabelName(label.patientName))) {
      return {
        success: false,
        method: "bridge",
        count: 0,
        error: MISSING_PATIENT_LABEL_NAME_ERROR,
      };
    }

    const deviceId = explicitDeviceId ?? this.getEffectiveDeviceId();
    if (!deviceId) {
      return {
        success: false,
        method: "bridge",
        count: 0,
        error:
          "No printer device configured. Please connect the Bridge app and select a printer.",
      };
    }
    const labelContents = labels.map((label) => ({
      content: buildXmlContent(label),
      labelType: "slide" as const,
      copies: 1,
    }));

    // ── Path 1: Local bridge (fast, no backend round-trip) ──
    const routingMode = await slidePrintRoutingService.resolveModeForPrint();
    const requiresBackend =
      routingMode === "backend_only" ||
      !!explicitDeviceId ||
      workstationPreferenceService.shouldUseBackendForDevice(
        "slide_printer",
        deviceId
      );
    if (deviceBridgeService.isLocal() && !requiresBackend) {
      try {
        const result = await deviceBridgeService.sendCommand(
          {
            type: "PRINT_SLIDE_LABELS",
            payload: { labels: labelContents, deviceId },
          },
          30000
        );
        const payload = result.payload ?? {};
        if (payload.success) {
          return {
            success: true,
            method: "bridge" as const,
            count: labels.length,
          };
        }
        const localError =
          (payload.error as string) || "Print failed via local bridge";
        if (this.isDeviceNotFoundError(localError)) {
          this.clearStaleDevice(deviceId);
        }
        return {
          success: false,
          method: "bridge" as const,
          count: 0,
          error: localError,
        };
      } catch {
        // Local bridge failed — fall through to backend path
      }
    }

    // ── Path 2: Backend relay (fallback) ──
    try {
      await bridgeService.createCommand({
        deviceId,
        commandType: "print_slide_labels",
        payload: { labels: labelContents },
        expiresInSeconds: 60,
      });

      return {
        success: true,
        method: "bridge",
        count: labels.length,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        (error instanceof Error ? error.message : "Failed to send to bridge");
      if (this.isDeviceNotFoundError(errorMessage)) {
        this.clearStaleDevice(deviceId);
      }
      return {
        success: false,
        method: "bridge",
        count: 0,
        error: errorMessage,
      };
    }
  }

  getStatusMessage(): string {
    switch (this.currentStatus) {
      case "folder-selected":
        return `Bridge printer: ${this.folderName ?? "Configured"}`;
      case "no-folder":
        return "No printer configured — connect Bridge app";
      case "browser-print":
        return "Using browser print dialog";
      case "unsupported":
        return "Bridge connection not available";
      default:
        return "Unknown status";
    }
  }

  disconnect(): void {
    this.deviceId = null;
    this.deviceName = null;
    this.currentStatus = "no-folder";
    this.notifyStatusChange();
    try {
      localStorage.removeItem(SLIDE_DEVICE_KEY);
      localStorage.removeItem(SLIDE_DEVICE_NAME_KEY);
    } catch {
      // localStorage unavailable; ignore
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private isDeviceNotFoundError(msg: string): boolean {
    return /device.*not found|not found.*device/i.test(msg);
  }

  private getEffectiveDeviceId(): string | null {
    if (
      workstationPreferenceService.hasInvalidPreferredPrinter("slide_printer")
    ) {
      return null;
    }
    const preferredPrinter =
      workstationPreferenceService.getPreferredPrinter("slide_printer");
    return (
      preferredPrinter?.deviceId ??
      workstationPreferenceService.getSelectedDeviceId("slide_printer") ??
      this.deviceId
    );
  }

  private clearStaleDevice(failedDeviceId: string): void {
    const preferredPrinter =
      workstationPreferenceService.getPreferredPrinter("slide_printer");
    if (preferredPrinter?.deviceId === failedDeviceId) {
      return;
    }
    this.deviceId = null;
    this.deviceName = null;
    this.currentStatus = "no-folder";
    try {
      localStorage.removeItem(SLIDE_DEVICE_KEY);
      localStorage.removeItem(SLIDE_DEVICE_NAME_KEY);
      localStorage.removeItem("bridge-device-slide_printer-id");
      localStorage.removeItem("bridge-device-slide_printer-name");
    } catch {
      // ignore
    }
    this.notifyStatusChange();
  }
}

export const slideLabelPrinterService = new SlideLabelPrinterService();
